import type { Business } from '@/types';
import type { LoginResponse } from '@/types/Menu';
import type { JewelryInvoiceResponse, JewelrySummary, JewelryDetailItem } from '@/types/jewelryInvoice';
import type { TokenContext, VoucherEntryCtx } from './dataTokens';

// Map the Redux login/app state to the Business model used by TokenContext.
export function mapAppToBusiness(app: LoginResponse | null | undefined): Business | null {
  if (!app) return null;
  return {
    id: app.branchId ?? '',
    name: app.companyName ?? '',
    address: app.companyAddress || app.branchAddress || '',
    branchName: app.branchName ?? '',
    branchAddress: app.branchAddress ?? '',
    panNumber: app.branchPanNumber ?? '',
    phone: app.branchNumber ?? '',
    email: app.branchEmail ?? '',
    logoDataUrl: app.branchPhoto ?? undefined,
    createdAt: 0,
    updatedAt: 0,
  };
}

const GRAMS_PER_TOLA = 11.664;

function mergeTola(summary: JewelrySummary, goldInTola?: number, silverInTola?: number): JewelrySummary {
  return {
    ...summary,
    goldRateInTola:   summary.goldRateInTola   || goldInTola   || summary.billGoldRate   * GRAMS_PER_TOLA,
    silverRateInTola: summary.silverRateInTola || silverInTola || summary.billSilverRate * GRAMS_PER_TOLA,
  };
}

function injectRatesIntoDetails(
  details: JewelryDetailItem[],
  summary: JewelrySummary,
): JewelryDetailItem[] {
  return details.map((item) => ({
    ...item,
    billGoldRate:     item.billGoldRate     ?? summary.billGoldRate,
    goldRateInTola:   item.goldRateInTola   ?? summary.goldRateInTola,
    billSilverRate:   item.billSilverRate   ?? summary.billSilverRate,
    silverRateInTola: item.silverRateInTola ?? summary.silverRateInTola,
  }));
}

export function mapJewelryResponseToTokenContext(
  response: JewelryInvoiceResponse,
  business: Business | null = null,
): TokenContext {
  const { summaryData, detailsData, goldRateInTola, silverRateInTola } = response.data;
  const summary = mergeTola(summaryData, goldRateInTola, silverRateInTola);
  return {
    business,
    invoice: null,
    summary,
    details: injectRatesIntoDetails(detailsData, summary),
  };
}

export interface VoucherPrintSummary {
  voucher_type?: string;
  voucher_number?: string;
  voucher_date?: string;
  narration?: string;
  [key: string]: unknown;
}

export interface VoucherPrintDetail {
  ledger_name?: string;
  group_name?: string;
  debit_amount?: number | string;
  credit_amount?: number | string;
  remarks?: string;
  [key: string]: unknown;
}

export interface VoucherPrintData {
  summaryData?: VoucherPrintSummary | null;
  detailsData?: VoucherPrintDetail[] | null;
}

export function mapVoucherPrintToTokenContext(
  printData: VoucherPrintData | null | undefined,
  business: Business | null = null,
): TokenContext {
  const summary = printData?.summaryData;
  if (!summary) {
    return {
      business,
      invoice: null,
      summary: null,
      details: null,
      voucher: null,
    };
  }
  const details = printData?.detailsData ?? [];
  return {
    business,
    invoice: null,
    summary: summary as unknown as JewelrySummary,
    details: details as unknown as JewelryDetailItem[],
    voucher: {
      voucherType: summary.voucher_type,
      voucherNo: summary.voucher_number,
      voucherDate: summary.voucher_date,
      narration: summary.narration,
      entries: details.map((row) => ({
        ledgerName: row.ledger_name ?? '',
        groupName: row.group_name,
        debitAmount: row.debit_amount ?? 0,
        creditAmount: row.credit_amount ?? 0,
        remarks: row.remarks,
        raw: row,
      })),
    },
  };
}

export function mapVoucherToTokenContext(
  voucher:
    | {
        voucherType?: string;
        voucherNo?: string;
        voucherDate?: string;
        narration?: string;
        voucherEntries?: VoucherEntryCtx[];
      }
    | null
    | undefined,
  business: Business | null = null,
): TokenContext {
  return {
    business,
    invoice: null,
    summary: null,
    details: null,
    voucher: voucher
      ? {
          voucherType: voucher.voucherType,
          voucherNo: voucher.voucherNo,
          voucherDate: voucher.voucherDate,
          narration: voucher.narration,
          entries: voucher.voucherEntries ?? [],
        }
      : null,
  };
}

export function mapRawSummaryToTokenContext(
  summaryData: JewelrySummary | null | undefined,
  detailsData: JewelryDetailItem[] | null | undefined = [],
  business: Business | null = null,
  goldRateInTola?: number,
  silverRateInTola?: number,
): TokenContext {
  const summary = summaryData ? mergeTola(summaryData, goldRateInTola, silverRateInTola) : null;
  return {
    business,
    invoice: null,
    summary,
    details: summary && detailsData?.length
      ? injectRatesIntoDetails(detailsData, summary)
      : detailsData ?? null,
  };
}
